import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { v4 as uuidv4 } from "uuid";

import { DATE_FORMATS_FNS, showNotification } from "components";
import { typedQueryClient, userKey } from "hooks";
import { PATHS } from "router/paths";
import { formatDate } from "utils/dateHelperFunctions";

import type { TFunction } from "i18next";
import type { Appointment } from "models";
import type { DayplannerAppointment } from "modules/Dayplanner";

type NotifyCustomerWaitingCarInShopParams = {
  userId?: number;
  oldAppointment?: Appointment | DayplannerAppointment;
  newAppointment?: Appointment | DayplannerAppointment;
};

type CanShowCustomerWaitingCarInShopNotificationParams = {
  oldAppointment: Appointment | DayplannerAppointment;
  newAppointment: Appointment | DayplannerAppointment;
};

type ShowCustomerWaitingCarInShopNotificationParams = {
  t: TFunction;
  appointment: Appointment | DayplannerAppointment;
};

export const canShowCustomerWaitingCarInShopNotification = ({ oldAppointment, newAppointment }: CanShowCustomerWaitingCarInShopNotificationParams) => {
  const appointmentCarInShop = oldAppointment.car_in_shop && !oldAppointment.car_out_of_shop;
  const appointmentUpdateCarInShop = newAppointment.car_in_shop && !newAppointment.car_out_of_shop && !appointmentCarInShop;
  const customerWaitingUpdate = newAppointment.customer_waiting && !oldAppointment.customer_waiting;

  return (appointmentUpdateCarInShop && oldAppointment.customer_waiting) || (customerWaitingUpdate && appointmentCarInShop);
};

export const showCustomerWaitingCarInShopNotification = ({ appointment, t }: ShowCustomerWaitingCarInShopNotificationParams) =>
  showNotification({
    id: `${uuidv4()} - ${appointment.id}`,
    time: formatDate(new Date(), DATE_FORMATS_FNS.hoursMinutes),
    title: `#${appointment.wo_nr}`,
    description: `${t("v8_status_updated_to", "Status Updated to")} ${t("v8_customer_waiting_and_car_in_shop", "Customer Waiting and Car In Shop")}`,
    path: `${window.location.origin}${PATHS.APPOINTMENTS}/${appointment.id}`,
    type: "AppointmentStatusNotification",
    icon: "mug saucer",
    appointment
  });

export const useCustomerWaitingCarInShopNotification = () => {
  const { t } = useTranslation();

  const notifyCustomerWaitingCarInShop = useCallback(
    ({ oldAppointment, newAppointment, userId }: NotifyCustomerWaitingCarInShopParams) => {
      const user = typedQueryClient.getQueryData(userKey);
      if (!user?.customer_waiting_car_in_shop_notification_enabled || user.id === userId || !oldAppointment || !newAppointment) return;

      if (canShowCustomerWaitingCarInShopNotification({ oldAppointment, newAppointment })) {
        showCustomerWaitingCarInShopNotification({ appointment: newAppointment, t });
      }
    },
    [t]
  );

  return { notifyCustomerWaitingCarInShop };
};
